// Carga de la nomina de todas las plantas (PP-70): una sola lectura para las
// dos solapas. Expone una RosterHandle por solapa para que la tabla de la
// nomina (RosterPanel) funcione igual que con una planta. Mismo patron que
// useRoster: una respuesta vieja se descarta y el error no entra al estado,
// solo su tipo.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ProviderRosterSite } from "@ucorp/contracts";
import type { PortalSession } from "../auth/session.js";
import { allSitesRows, type AllSitesDriverRow, type AllSitesVehicleRow } from "../logic/roster.js";
import { FleetError, type FleetPort } from "./port.js";
import type { RosterHandle, RosterState } from "./useRoster.js";

type Loaded = {
  today: string;
  sites: readonly ProviderRosterSite[];
  vehicles: readonly AllSitesVehicleRow[];
  drivers: readonly AllSitesDriverRow[];
};
type State = { estado: "cargando" } | { estado: "denegado" } | { estado: "error" } | ({ estado: "listo" } & Loaded);

export interface AllSitesRoster {
  sites: readonly ProviderRosterSite[];
  vehicles: RosterHandle<AllSitesVehicleRow>;
  drivers: RosterHandle<AllSitesDriverRow>;
}

function listOf<K extends "vehicles" | "drivers">(state: State, key: K): RosterState<Loaded[K][number]> {
  if (state.estado !== "listo") return state;
  return { estado: "listo", today: state.today, items: state[key] };
}

export function useAllSitesRoster(port: Pick<FleetPort, "listRoster">, session: PortalSession): AllSitesRoster {
  const [state, setState] = useState<State>({ estado: "cargando" });
  const [round, setRound] = useState(0);
  const silentNext = useRef(false);

  useEffect(() => {
    let current = true;
    const silent = silentNext.current;
    silentNext.current = false;
    if (!silent) setState({ estado: "cargando" });
    port
      .listRoster(session)
      .then((roster) => {
        if (current) setState({ estado: "listo", today: roster.today ?? "", sites: roster.sites, ...allSitesRows(roster) });
      })
      .catch((error: unknown) => {
        if (current) setState({ estado: error instanceof FleetError && error.kind === "access" ? "denegado" : "error" });
      });
    return () => {
      current = false;
    };
  }, [port, session, round]);

  const reload = useCallback(() => setRound((n) => n + 1), []);
  const refresh = useCallback(() => {
    silentNext.current = true;
    setRound((n) => n + 1);
  }, []);
  const updateVehicles = useCallback((change: (items: readonly AllSitesVehicleRow[]) => readonly AllSitesVehicleRow[]) => {
    setState((previous) => (previous.estado === "listo" ? { ...previous, vehicles: change(previous.vehicles) } : previous));
  }, []);
  const updateDrivers = useCallback((change: (items: readonly AllSitesDriverRow[]) => readonly AllSitesDriverRow[]) => {
    setState((previous) => (previous.estado === "listo" ? { ...previous, drivers: change(previous.drivers) } : previous));
  }, []);

  return useMemo(
    () => ({
      sites: state.estado === "listo" ? state.sites : [],
      vehicles: { state: listOf(state, "vehicles"), reload, refresh, update: updateVehicles },
      drivers: { state: listOf(state, "drivers"), reload, refresh, update: updateDrivers },
    }),
    [state, reload, refresh, updateVehicles, updateDrivers],
  );
}
